import React from 'react'

import __partition from 'lodash/partition'
import __uniqueId from 'lodash/uniqueId'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import type {
  ActionMeta,
  GroupBase,
  OptionProps,
  Options,
  OptionsOrGroups,
  SelectComponentsConfig,
  SingleValue,
} from 'react-select'
import { components as reactSelectComponents } from 'react-select'
import CreatableSelect from 'react-select/creatable'

import { dashboardActions } from '@services'

import { bindActionToPromise, parseApiErrorMessage } from '@helpers'

import { useAlertDispatch } from '@contexts/AlertProvider'

import { usePromptTextCreator } from '@hooks'

import { Typography } from '@components/ui'

import { LEDGER_NUMBER_WITH_OPTIONAL_NAME_REGEX, OPTIONAL_DIGITS_REGEX } from '@constants'

import {
  compareOption,
  createOptions,
  createSingleOption,
  getNewOptionData,
  getValueComponents,
  groupedCompareOption,
} from './helpers'
import type { SelectOption } from './types'

import { selectFormStyles as customReactSelectStyles } from '@styles/select'
import { formSelectMessages, recommendationMessages } from '@messages'

const PAID_THROUGH_LEDGER_NUMBER_START_CODE = '38' // pénzeszközök főkönyvi száma 38-cal kezdődik

// need to customize the recommended option
const CustomSelectOption = reactSelectComponents.Option as unknown as React.ComponentType<
  OptionProps<SelectOption, false, GroupBase<SelectOption>>
>

function CustomLedgerNumberOption({ children, ...rest }: OptionProps<SelectOption, false, GroupBase<SelectOption>>) {
  const { name, isNew } = rest.data.data
  return (
    <CustomSelectOption {...rest}>
      <div className="custom-option">
        <Typography size="400-sm">{children}</Typography>
        {!isNew && name && <Typography size="400-xs">{name}</Typography>}
      </div>
    </CustomSelectOption>
  )
}

const componentOverridesProp: {
  components: SelectComponentsConfig<SelectOption, false, GroupBase<SelectOption>>
} = {
  components: {
    Option: CustomLedgerNumberOption,
  },
}

export interface LedgerNumberCreatableSelectProps {
  className?: string
  createNewOption: AsyncFunction<Omit<LedgerNumber, 'id'>, LedgerNumber>
  disabled?: boolean
  isPaidThroughLedgerNumber?: boolean
  name: string
  onChange: (value: Nullable<LedgerNumberFormValue>) => void
  onFocus?: VoidFunction
  options: LedgerNumber[]
  placeholder?: React.ReactNode
  recommendations?: { data: number[]; loading: boolean }
  suggestion?: LedgerNumberFormValue
  value: Nullable<string>
}

function PureLedgerNumberCreatableSelect({
  className,
  createNewOption,
  disabled = false,
  isPaidThroughLedgerNumber = false,
  name,
  onChange,
  onFocus,
  options,
  placeholder,
  recommendations,
  suggestion,
  value,
}: LedgerNumberCreatableSelectProps) {
  const { formatMessage } = useIntl()
  const [isLoading, setLoading] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const { setErrorAlert } = useAlertDispatch()
  const promptTextCreator = usePromptTextCreator()

  const selectValue = value ? createSingleOption(value) : null
  const recommendationsLoading = recommendations?.loading ?? false

  const selectOptions = React.useMemo(() => {
    if (recommendationsLoading) {
      return []
    }

    let filteredOptions: LedgerNumberFormValue[] = options

    if (isPaidThroughLedgerNumber) {
      filteredOptions = options.filter(option => option.code?.startsWith(PAID_THROUGH_LEDGER_NUMBER_START_CODE))
    }

    if (suggestion) {
      filteredOptions = filteredOptions.filter(
        option => option.code !== suggestion.code && option.name !== suggestion.name
      )
    }

    const recommendationsData = recommendations?.data ?? []

    // separate options and recommendations
    const [recommendedOptions, existingOptions] = __partition(filteredOptions, function (option) {
      return option.id && recommendationsData.includes(option.id)
    })

    if (suggestion) {
      recommendedOptions.push(suggestion)
    }

    if (recommendedOptions.length > 0) {
      return [
        {
          label: formatMessage(recommendationMessages.recommendationsLabel),
          options: createOptions(recommendedOptions),
        },
        {
          label: formatMessage(recommendationMessages.optionsLabel),
          options: createOptions(existingOptions),
        },
      ]
    }
    return createOptions(existingOptions)
  }, [formatMessage, isPaidThroughLedgerNumber, options, recommendations?.data, recommendationsLoading, suggestion])

  const handleChange = React.useCallback(
    (option: SingleValue<SelectOption>, { action }: ActionMeta<SelectOption>) => {
      if (action === 'select-option' && option) {
        onChange(option.data) // set to form field
      }
      if (action === 'clear') {
        onChange(null) // set to form fields
      }
    },
    [onChange]
  )

  const handleInputChange = React.useCallback((rawValue: string) => {
    const inputValue =
      (rawValue.length >= 2 && rawValue.match(LEDGER_NUMBER_WITH_OPTIONAL_NAME_REGEX)) || // already entered minimum 2 digits
      (rawValue.length < 2 && rawValue.match(OPTIONAL_DIGITS_REGEX)) // enter at least 2 digits first
        ? rawValue
        : rawValue.substring(0, rawValue.length - 1)
    setInputValue(inputValue)
  }, [])

  const handleCreateOption = React.useCallback(
    async (inputValue: string) => {
      setLoading(true)
      const payload = getValueComponents(inputValue)
      try {
        const result = await createNewOption(payload)
        onChange(result)
      } catch (error) {
        const errorMsg = parseApiErrorMessage(error)
        // display error in snackalert
        setErrorAlert(errorMsg)
      }
      setLoading(false)
    },
    [createNewOption, onChange, setErrorAlert]
  )

  const noOptionsMessage = React.useCallback(() => {
    if (recommendationsLoading) {
      return formatMessage(formSelectMessages.asyncLoadingText)
    }

    if (!selectOptions.length) {
      if (isPaidThroughLedgerNumber) {
        return formatMessage(formSelectMessages.paidThroughLedgerNumberEmptyCreateText)
      }

      return formatMessage(formSelectMessages.ledgerNumberEmptyCreateText)
    }
    return formatMessage(formSelectMessages.selectNoResultsText)
  }, [formatMessage, isPaidThroughLedgerNumber, recommendationsLoading, selectOptions.length])

  const isValidNewOption = React.useCallback(
    (
      inputValue: string,
      selectValue: Nullable<Options<SelectOption>>,
      selectOptions: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>
    ) => {
      // returns true when
      // inputValue is not empty or
      // selectValue is not null or
      // inputValue length is too short (minimum 2)
      // if paidThroughLedgerNumber is true, inputValue must start with '38'
      // selectValue does not match with entered inputValue or
      // select has no options what matches the inputValue
      return !(
        !inputValue ||
        !selectValue ||
        inputValue.length < 2 ||
        (isPaidThroughLedgerNumber && !inputValue.startsWith(PAID_THROUGH_LEDGER_NUMBER_START_CODE)) ||
        selectValue.some(function (option) {
          return compareOption(inputValue, option)
        }) ||
        selectOptions.some(function (option) {
          return groupedCompareOption(inputValue, option)
        })
      )
    },
    [isPaidThroughLedgerNumber]
  )

  return (
    <CreatableSelect
      {...componentOverridesProp}
      className={className}
      classNamePrefix="react-select"
      formatCreateLabel={promptTextCreator}
      getNewOptionData={getNewOptionData}
      inputId={name}
      inputValue={inputValue}
      instanceId={__uniqueId('rs')}
      isClearable
      isDisabled={disabled}
      isLoading={isLoading}
      isValidNewOption={isValidNewOption}
      noOptionsMessage={noOptionsMessage}
      onChange={handleChange}
      onCreateOption={handleCreateOption}
      onFocus={onFocus}
      onInputChange={handleInputChange}
      options={selectOptions}
      placeholder={placeholder ?? formatMessage(formSelectMessages.selectPlaceholder)}
      styles={customReactSelectStyles}
      value={selectValue}
    />
  )
}

export const LedgerNumberCreatableSelect = connect(
  (state: Store) => ({
    options: state.dashboard.ledgerNumbers,
  }),
  dispatch => ({
    createNewOption: bindActionToPromise(dispatch, dashboardActions.createLedgerNumber.request),
  })
)(PureLedgerNumberCreatableSelect)

LedgerNumberCreatableSelect.displayName = 'LedgerNumberCreatableSelect'
